import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { convertStringToDate, formatDateByTimeZone } from "~/utils/dateTimeUtils";
import type {
  ActivationData,
  ActivationCheckResponse,
  PhoneBooksData,
  PhoneBookAutoUpdateEntity,
  PhoneBooksEntity,
  AppSettingAutoUpdateObject,
  SettingCommonEntity,
  StandbyEntity,
  CallInfoEntity,
  ReceptionCommonWordEntity,
  MenuWordEntity,
  DeliveryWordEntity,
  SettingMenuButtonEntity,
  SettingBasicEntity,
  AppSettingPayload,
  AppSettingsResponse,
} from "~/utils/receptionDB.types";

export type {
  ActivationData,
  ActivationCheckResponse,
  PhoneBooksData,
  PhoneBooksEntity,
  PhoneBookAutoUpdateEntity,
  AppSettingAutoUpdateObject,
  SettingCommonEntity,
  StandbyEntity,
  CallInfoEntity,
  ReceptionCommonWordEntity,
  MenuWordEntity,
  DeliveryWordEntity,
  SettingMenuButtonEntity,
  SettingBasicEntity,
  MenuWords,
  DeliveryWords,
  ScreenWords,
  AppSettingPayload,
  AppSettingsResponse,
  ReceptionByAppointmentResponse,
} from "~/utils/receptionDB.types";

interface ActivationDBSchema extends DBSchema {
  activation: {
    key: string;
    value: ActivationData;
  };
  phoneBooks: {
    key: string;
    value: PhoneBooksEntity;
  };
  phoneBookAutoUpdate: {
    key: string;
    value: PhoneBookAutoUpdateEntity;
  };
  appSettingAutoUpdate: {
    key: string;
    value: AppSettingAutoUpdateObject;
  };
  settingCommon: {
    key: string;
    value: SettingCommonEntity;
  };
  standby: {
    key: string;
    value: StandbyEntity;
  };
  callInfo: {
    key: string;
    value: CallInfoEntity;
  };
  receptionCommonWord: {
    key: string;
    value: ReceptionCommonWordEntity;
  };
  menuWords: {
    key: string;
    value: MenuWordEntity[];
  };
  deliveryWords: {
    key: string;
    value: DeliveryWordEntity[];
  };
  menuButtons: {
    key: string;
    value: SettingMenuButtonEntity[];
  };
  basics: {
    key: string;
    value: SettingBasicEntity[];
  };
  deliveryMapImage: {
    key: string;
    value: DeliveryMapImageCacheEntity;
  };
}

/**
 * deliveryMapImagePath の署名付きURLは期限切れになるため、
 * 取得したBlobをローカルにキャッシュして表示時に利用する。
 */
export interface DeliveryMapImageCacheEntity {
  originalUrl: string;
  blob: Blob;
  fetchedAt: number;
}

const DB_NAME = "activation-db";
const DB_VERSION = 6;
const ACTIVATION_STORE = "activation" as const;
const PHONE_BOOKS_STORE = "phoneBooks" as const;
const PHONE_BOOK_AUTO_UPDATE_STORE = "phoneBookAutoUpdate" as const;
const APP_SETTING_AUTO_UPDATE_STORE = "appSettingAutoUpdate" as const;
const SETTING_COMMON_STORE = "settingCommon" as const;
const STANDBY_STORE = "standby" as const;
const CALL_INFO_STORE = "callInfo" as const;
const RECEPTION_COMMON_WORD_STORE = "receptionCommonWord" as const;
const MENU_WORDS_STORE = "menuWords" as const;
const DELIVERY_WORDS_STORE = "deliveryWords" as const;
const MENU_BUTTONS_STORE = "menuButtons" as const;
const BASICS_STORE = "basics" as const;
const DELIVERY_MAP_IMAGE_STORE = "deliveryMapImage" as const;
const RECORD_KEY = "current";

const APP_SETTING_STORES = [
  SETTING_COMMON_STORE,
  STANDBY_STORE,
  CALL_INFO_STORE,
  RECEPTION_COMMON_WORD_STORE,
  MENU_WORDS_STORE,
  DELIVERY_WORDS_STORE,
  MENU_BUTTONS_STORE,
  BASICS_STORE,
] as const;

const MENU_SETTING_STORES = [
  MENU_WORDS_STORE,
  DELIVERY_WORDS_STORE,
  MENU_BUTTONS_STORE,
  BASICS_STORE,
] as const;

const PHONEBOOK_AND_MENU_SETTING_STORES = [PHONE_BOOKS_STORE, ...MENU_SETTING_STORES] as const;

export interface AppSettingsNormalized {
  settingCommon?: SettingCommonEntity;
  standby?: StandbyEntity;
  callInfo?: CallInfoEntity;
  receptionCommonWord?: ReceptionCommonWordEntity;
  menuWords: MenuWordEntity[];
  deliveryWords: DeliveryWordEntity[];
  menuButtons: SettingMenuButtonEntity[];
  basics: SettingBasicEntity[];
}

/**
 * IndexedDBデータベース接続を取得する。
 * バージョン変更時にすべての必要なオブジェクトストアを自動作成する。
 */
const getDB = (): Promise<IDBPDatabase<ActivationDBSchema>> =>
  openDB<ActivationDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(ACTIVATION_STORE)) {
        db.createObjectStore(ACTIVATION_STORE);
      }
      if (!db.objectStoreNames.contains(PHONE_BOOKS_STORE)) {
        db.createObjectStore(PHONE_BOOKS_STORE);
      }
      if (!db.objectStoreNames.contains(PHONE_BOOK_AUTO_UPDATE_STORE)) {
        db.createObjectStore(PHONE_BOOK_AUTO_UPDATE_STORE);
      }
      if (!db.objectStoreNames.contains(APP_SETTING_AUTO_UPDATE_STORE)) {
        db.createObjectStore(APP_SETTING_AUTO_UPDATE_STORE);
      }
      APP_SETTING_STORES.forEach((storeName) => {
        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName);
        }
      });
      if (!db.objectStoreNames.contains(DELIVERY_MAP_IMAGE_STORE)) {
        db.createObjectStore(DELIVERY_MAP_IMAGE_STORE);
      }
    },
  });

/**
 * アクティベーションデータをIndexedDBに保存する。
 * expireDateをタイムゾーンに基づいて正規化してから保存する。
 * @param data - 保存するアクティベーションデータ
 * @throws {Error} expireDateのフォーマットが無効な場合
 */
export const saveActivationData = async (data: ActivationData): Promise<void> => {
  const parsedExpireDate = convertStringToDate(data.expireDate, data.timezone, "yyyy-MM-dd");
  if (!parsedExpireDate) {
    throw new Error("Invalid expireDate format");
  }

  const normalizedData: ActivationData = {
    ...data,
    expireDate: formatDateByTimeZone(parsedExpireDate, data.timezone, "yyyy-MM-dd"),
  };

  const db = await getDB();
  await db.put(ACTIVATION_STORE, normalizedData, RECORD_KEY);
};

/**
 * IndexedDBからアクティベーションデータを取得する。
 * @returns アクティベーションデータ。存在しない場合はundefined
 */
export const getActivationData = async (): Promise<ActivationData | undefined> => {
  const db = await getDB();
  return db.get(ACTIVATION_STORE, RECORD_KEY);
};

/**
 * activation/check の応答から ActivationData を再構築して保存する（IndexedDB が空の場合のみ）。
 *
 * IndexedDB の activation ストアだけが失われ、localStorage(receptionUuid) は残存する、という
 * ブラウザ起因のデータロスト時に、待受フローから activation を自動復旧するための処理。
 * expireDate / maxUser / timezone / contractLanguages は activation/check 応答から、
 * plan と各種オプションフラグは保存済みの電話帳エンティティから補完する。
 * plan / フラグは電話帳ダウンロード時の syncPhoneBooksData で最終的に再同期される。
 *
 * @param check - activation/check のレスポンス
 * @returns 復元を行った場合は true、不要・不能だった場合は false
 */
export const restoreActivationDataIfMissing = async (
  check: ActivationCheckResponse
): Promise<boolean> => {
  try {
    const existing = await getActivationData();
    if (existing) return false;

    const activation = check?.activation;
    const expireDate = activation?.expireDate;
    const maxUser = activation?.maxUser ?? null;
    const timezone = activation?.timezone;
    const contractLanguages = activation?.contractLanguages;

    // timezone / expireDate が無いと saveActivationData(expireDate のパース)が成立しないため復元しない
    if (!timezone || !expireDate) return false;

    const phoneBook = await getPhoneBooksEntity();
    const restored: ActivationData = {
      expireDate,
      maxUser: maxUser ?? 0,
      timezone,
      contractLanguages: contractLanguages ?? [],
      plan: phoneBook?.plan ?? "",
      deviceName: "",
      isAppointmentEnabled: phoneBook?.isAppointmentEnabled ?? false,
      isFacialIdentificationEnabled: phoneBook?.isFacialIdentificationEnabled ?? false,
      isSelectCompanyEnabled: phoneBook?.isSelectCompanyEnabled ?? false,
      isAppointmentHistoryEnabled: phoneBook?.isAppointmentHistoryEnabled ?? false,
    };

    await saveActivationData(restored);
    return true;
  } catch (e) {
    console.error("Failed to restore ActivationData to IndexedDB:", e);
    return false;
  }
};

/**
 * 電話帳エンティティをIndexedDBに保存する。
 * @param data - 保存するPhoneBooksEntity（groupList・userListを含む）
 */
export const savePhoneBooksEntity = async (data: PhoneBooksEntity): Promise<void> => {
  const db = await getDB();
  await db.put(PHONE_BOOKS_STORE, data, RECORD_KEY);
};

/**
 * IndexedDBから電話帳エンティティを取得する。
 * @returns PhoneBooksEntity。存在しない場合はundefined
 */
export const getPhoneBooksEntity = async (): Promise<PhoneBooksEntity | undefined> => {
  const db = await getDB();
  return db.get(PHONE_BOOKS_STORE, RECORD_KEY);
};

/**
 * S3から取得したJSONデータとAPIレスポンスのメタデータを組み合わせてPhoneBooksEntityを構築する。
 * S3データにgroups/groupUsersが存在しない場合は空配列にフォールバックする。
 * @param s3Data - S3エンドポイントから取得したJSONオブジェクト（groups, groupUsersキーを含む）
 * @param phoneBooksData - APIレスポンスの電話帳メタデータ
 * @returns 構築されたPhoneBooksEntity
 */
export const buildPhoneBooksEntityFromS3Data = (
  s3Data: Record<string, unknown>,
  phoneBooksData: PhoneBooksData
): PhoneBooksEntity => {
  return {
    phoneBookId: phoneBooksData.phoneBookId,
    plan: phoneBooksData.plan,
    isSelectCompanyEnabled: phoneBooksData.isSelectCompanyEnabled,
    isAppointmentEnabled: phoneBooksData.isAppointmentEnabled,
    isFacialIdentificationEnabled: phoneBooksData.isFacialIdentificationEnabled,
    isAppointmentHistoryEnabled: phoneBooksData.isAppointmentHistoryEnabled,
    groupList: (s3Data.groups as PhoneBooksEntity["groupList"]) ?? [],
    userList: (s3Data.groupUsers as PhoneBooksEntity["userList"]) ?? [],
  };
};

/**
 * S3プリサインドURLから電話帳データをフェッチし、IndexedDBに保存する。
 *
 * @param responseDataUrl - S3プリサインドURL（string以外の場合は無効扱い）
 * @param phoneBooksData - APIレスポンスの電話帳メタデータ
 * @returns 保存されたPhoneBooksEntity。URLが無効・HTTPエラー・例外発生時はundefined
 */
export const fetchAndSavePhoneBooksEntity = async (
  responseDataUrl: unknown,
  phoneBooksData: PhoneBooksData,
  isSave: boolean = true
): Promise<PhoneBooksEntity | undefined> => {
  if (!responseDataUrl || typeof responseDataUrl !== "string") {
    return undefined;
  }

  try {
    const s3Response = await fetch(responseDataUrl);
    if (s3Response.ok) {
      const s3Data = (await s3Response.json()) as Record<string, unknown>;
      const phoneBooksEntity = buildPhoneBooksEntityFromS3Data(s3Data, phoneBooksData);
      if (isSave) {
        await savePhoneBooksEntity(phoneBooksEntity);
      }
      return phoneBooksEntity;
    }
  } catch (error) {
    console.error("Failed to fetch and save phonebook entity:", error);
  }

  return undefined;
};

/**
 * 電話帳自動更新データをIndexedDBに保存する。
 * updateDate は予約時刻/表示用として更新し、lastUpdateDate は維持する。
 * @param updateDate - 新しい更新日時文字列
 */
export const savePhoneBookAutoUpdateData = async (updateDate: string): Promise<void> => {
  const db = await getDB();
  const current = await db.get(PHONE_BOOK_AUTO_UPDATE_STORE, RECORD_KEY);
  const payload: PhoneBookAutoUpdateEntity = {
    updateDate,
    lastUpdateDate: current?.lastUpdateDate ?? null,
  };
  await db.put(PHONE_BOOK_AUTO_UPDATE_STORE, payload, RECORD_KEY);
};

/**
 * 電話帳自動更新データを初期化して保存する。
 * lastUpdateDate は常に null に設定する（新しいアクティベーション時の初期設定用）。
 * @param updateDate - 新しい更新日時文字列、存在しない場合は null をセットする
 */
export const initPhoneBookAutoUpdateData = async (updateDate: string | null): Promise<void> => {
  const db = await getDB();
  const payload: PhoneBookAutoUpdateEntity = {
    updateDate,
    lastUpdateDate: null,
  };
  await db.put(PHONE_BOOK_AUTO_UPDATE_STORE, payload, RECORD_KEY);
};

/**
 * IndexedDBから電話帳自動更新データを取得する。
 * @returns PhoneBookAutoUpdateEntity。存在しない場合はundefined
 */
export const getPhoneBookAutoUpdateData = async (): Promise<
  PhoneBookAutoUpdateEntity | undefined
> => {
  const db = await getDB();
  return db.get(PHONE_BOOK_AUTO_UPDATE_STORE, RECORD_KEY);
};

/**
 * アプリ設定自動更新データをIndexedDBに保存する。
 * updateDate は予約時刻/表示用として更新し、lastUpdateDate は維持する。
 * @param updateDate - 新しい更新日時文字列
 */
export const saveAppSettingAutoUpdateData = async (updateDate: string): Promise<void> => {
  const db = await getDB();
  const current = await db.get(APP_SETTING_AUTO_UPDATE_STORE, RECORD_KEY);
  const payload: AppSettingAutoUpdateObject = {
    updateDate,
    lastUpdateDate: current?.lastUpdateDate ?? null,
  };
  await db.put(APP_SETTING_AUTO_UPDATE_STORE, payload, RECORD_KEY);
};

/**
 * アプリ設定自動更新データを初期化して保存する。
 * lastUpdateDate は常に null に設定する（新しいアクティベーション時の初期設定用）。
 * @param updateDate - 新しい更新日時文字列、存在しない場合は null をセットする
 */
export const initAppSettingAutoUpdateData = async (updateDate: string | null): Promise<void> => {
  const db = await getDB();
  const payload: AppSettingAutoUpdateObject = {
    updateDate,
    lastUpdateDate: null,
  };
  await db.put(APP_SETTING_AUTO_UPDATE_STORE, payload, RECORD_KEY);
};

/**
 * IndexedDBからアプリ設定自動更新データを取得する。
 * @returns AppSettingAutoUpdateObject。存在しない場合はundefined
 */
export const getAppSettingAutoUpdateData = async (): Promise<
  AppSettingAutoUpdateObject | undefined
> => {
  const db = await getDB();
  return db.get(APP_SETTING_AUTO_UPDATE_STORE, RECORD_KEY);
};

/**
 * IndexedDBから電話帳エンティティを削除する。
 */
export const clearPhoneBooksData = async (): Promise<void> => {
  const db = await getDB();
  await db.delete(PHONE_BOOKS_STORE, RECORD_KEY);
};

/**
 * すべての IndexedDB ストアのデータを一括削除する。
 * activation/check でアクティベーション無効が確認された場合のリセット用。
 */
export const clearAllReceptionData = async (): Promise<void> => {
  const db = await getDB();
  const allStores = [
    ACTIVATION_STORE,
    PHONE_BOOKS_STORE,
    PHONE_BOOK_AUTO_UPDATE_STORE,
    APP_SETTING_AUTO_UPDATE_STORE,
    ...APP_SETTING_STORES,
    DELIVERY_MAP_IMAGE_STORE,
  ] as const;
  const tx = db.transaction(allStores, "readwrite");
  allStores.forEach((storeName) => {
    // deliveryMapImage は会社IDをキーに複数エントリを持つため clear() で全削除する。
    // 他のストアは RECORD_KEY 単一エントリなので delete(RECORD_KEY) で十分。
    if (storeName === DELIVERY_MAP_IMAGE_STORE) {
      tx.objectStore(storeName).clear();
    } else {
      tx.objectStore(storeName).delete(RECORD_KEY);
    }
  });
  await tx.done;
};

/**
 * AppSettingPayloadから共通設定エンティティ（言語・営業時間など）を生成する。
 * groupDisplayId=0のペイロードにのみ存在するデータを対象とする。
 * @throws {Error} appSetting.commonが存在しない場合
 */
const createSettingCommonEntity = (payload: AppSettingPayload): SettingCommonEntity => {
  const common = payload.appSetting.common;
  if (!common) {
    throw new Error("Missing appSetting.common for groupDisplayId=0");
  }
  return {
    id: 0,
    settingLanguageCode: common.settingLanguageCode,
    isDisplayLanguageButton: common.isDisplayLanguageButton,
    receptionLanguageCode: common.receptionLanguageCode,
    enableAutoSleep: common.enableAutoSleep,
    openingHours: common.openingHours,
    closingHours: common.closingHours,
  };
};

/**
 * AppSettingPayloadからスタンバイ画面設定エンティティを生成する。
 * 待受画像タイプ・メッセージ・アニメーション設定などを含む。
 * @throws {Error} appSetting.standbyが存在しない場合
 */
const createStandbyEntity = (payload: AppSettingPayload): StandbyEntity => {
  const standby = payload.appSetting.standby;
  if (!standby) {
    throw new Error("Missing appSetting.standby for groupDisplayId=0");
  }
  return {
    id: 0,
    standbyImageType: standby.standbyImageType,
    defaultStandbyImageType: standby.defaultStandbyImageType,
    standbyImageSwitchInterval: standby.standbyImageSwitchInterval,
    returnBackgroundTime: standby.returnBackgroundTime,
    firstMessageText: standby.firstMessageText,
    secondMessageText: standby.secondMessageText,
    thirdMessageText: standby.thirdMessageText,
    appTheme: standby.appTheme,
    standbyTextColor: standby.standbyTextColor,
    animationType: standby.animationType,
    flashTime: standby.flashTime,
    scrollTime: standby.scrollTime,
    firstMessageScrollEnable: standby.firstMessageScrollEnable,
    secondMessageScrollEnable: standby.secondMessageScrollEnable,
    thirdMessageScrollEnable: standby.thirdMessageScrollEnable,
    isDisplayFingerIcon: standby.isDisplayFingerIcon,
  };
};

/**
 * AppSettingPayloadから通話設定エンティティを生成する。
 * SIPサーバー接続情報・音声設定・タイムアウト設定などを含む。
 * @throws {Error} appSetting.callが存在しない場合
 */
const createCallInfoEntity = (payload: AppSettingPayload): CallInfoEntity => {
  const call = payload.appSetting.call;
  if (!call) {
    throw new Error("Missing appSetting.call for groupDisplayId=0");
  }
  return {
    id: 0,
    callLibrary: call.callLibrary,
    name: call.name,
    password: call.password,
    phoneNumber: call.phoneNumber,
    serverAddress: call.serverAddress,
    serverPort: call.serverPort,
    clientPort: call.clientPort,
    displayName: call.displayName,
    callDisconnectTime: call.callDisconnectTime,
    audioSessionMode: call.audioSessionMode,
    audioUnitSubType: call.audioUnitSubType,
    samplingRate: call.samplingRate,
    buffer: call.buffer,
    switchBoardGain: call.switchBoardGain,
    expiresValue: call.expiresValue,
    secondsRetryAfterFail: call.secondsRetryAfterFail,
    ptimeMs: call.ptimeMs,
  };
};

/**
 * AppSettingPayloadから受付共通ワードエンティティを生成する。
 * 全グループ共通の画面表示テキスト（多言語対応）を格納する。
 * @throws {Error} appSetting.wordsが存在しない場合
 */
const createReceptionCommonWordEntity = (payload: AppSettingPayload): ReceptionCommonWordEntity => {
  const words = payload.appSetting.words;
  if (!words) {
    throw new Error(`Missing appSetting.words for groupDisplayId=${payload.groupDisplayId}`);
  }
  return {
    id: 0,
    items: words.map((word) => ({
      languageCode: word.languageCode,
      screenWords: word.screenWords,
    })),
  };
};

/**
 * AppSettingPayloadからメニューワードエンティティを生成する。
 * グループ別のメニュー表示テキスト（多言語対応）を格納する。
 * @throws {Error} appSetting.wordsが存在しない場合
 */
const createMenuWordEntity = (payload: AppSettingPayload): MenuWordEntity => {
  const words = payload.appSetting.words;
  if (!words) {
    throw new Error(`Missing appSetting.words for groupDisplayId=${payload.groupDisplayId}`);
  }
  return {
    id: payload.groupDisplayId,
    items: words.map((word) => ({
      languageCode: word.languageCode,
      menuWords: word.menuWords,
    })),
  };
};

/**
 * AppSettingPayloadから配達ワードエンティティを生成する。
 * グループ別の配達関連表示テキスト（多言語対応）を格納する。
 * @throws {Error} appSetting.wordsが存在しない場合
 */
const createDeliveryWordEntity = (payload: AppSettingPayload): DeliveryWordEntity => {
  const words = payload.appSetting.words;
  if (!words) {
    throw new Error(`Missing appSetting.words for groupDisplayId=${payload.groupDisplayId}`);
  }
  return {
    id: payload.groupDisplayId,
    items: words.map((word) => ({
      languageCode: word.languageCode,
      deliveryWords: word.deliveryWords,
    })),
  };
};

/**
 * AppSettingPayloadからメニューボタン設定エンティティを生成する。
 * グループ別のボタン表示・通知タイプ・内線番号などを含む。
 * @throws {Error} appSetting.menuButtonが存在しない場合
 */
const createSettingMenuButtonEntity = (payload: AppSettingPayload): SettingMenuButtonEntity => {
  const menuButton = payload.appSetting.menuButton;
  if (!menuButton) {
    throw new Error(`Missing appSetting.menuButton for groupDisplayId=${payload.groupDisplayId}`);
  }
  return {
    id: payload.groupDisplayId,
    buttonOrder: menuButton.buttonOrder,
    isDisplayMenu: menuButton.isDisplayMenu,
    isDisplayAppointmentExists: menuButton.isDisplayAppointmentExists,
    isDisplayVisitorInfoGroup: menuButton.isDisplayVisitorInfoGroup,
    isDisplayVisitorInfoUser: menuButton.isDisplayVisitorInfoUser,
    isDisplayVisitorInfoRecruit: menuButton.isDisplayVisitorInfoRecruit,
    isDisplayVisitorInfoOther: menuButton.isDisplayVisitorInfoOther,
    isDisplayVisitorInfoSecretary: menuButton.isDisplayVisitorInfoSecretary,
    deliveryTransitionType: menuButton.deliveryTransitionType,
    deliveryMapImagePath: menuButton.deliveryMapImagePath,
    isDisplayInputExtensionNumber: menuButton.isDisplayInputExtensionNumber,
    recruitNotificationType: menuButton.recruitNotificationType,
    recruitExtensionNumber: menuButton.recruitExtensionNumber,
    recruitCallType: menuButton.recruitCallType,
    recruitFaceTimeAddress: menuButton.recruitFaceTimeAddress,
    deliveryNotificationType: menuButton.deliveryNotificationType,
    deliveryExtensionNumber: menuButton.deliveryExtensionNumber,
    deliveryCallType: menuButton.deliveryCallType,
    deliveryFaceTimeAddress: menuButton.deliveryFaceTimeAddress,
    otherNotificationType: menuButton.otherNotificationType,
    otherExtensionNumber: menuButton.otherExtensionNumber,
    otherCallType: menuButton.otherCallType,
    otherFaceTimeAddress: menuButton.otherFaceTimeAddress,
    taxiNotificationType: menuButton.taxiNotificationType,
    taxiExtensionNumber: menuButton.taxiExtensionNumber,
    taxiCallType: menuButton.taxiCallType,
    taxiFaceTimeAddress: menuButton.taxiFaceTimeAddress,
    secretaryNotificationType: menuButton.secretaryNotificationType,
    secretaryExtensionNumber: menuButton.secretaryExtensionNumber,
    secretaryCallType: menuButton.secretaryCallType,
    secretaryFaceTimeAddress: menuButton.secretaryFaceTimeAddress,
  };
};

/**
 * AppSettingPayloadから基本設定エンティティを生成する。
 * グループ別のロゴ表示・入力必須項目・電話帳有効フラグなどを含む。
 * @throws {Error} appSetting.basicが存在しない場合
 */
const createSettingBasicEntity = (payload: AppSettingPayload): SettingBasicEntity => {
  const basic = payload.appSetting.basic;
  if (!basic) {
    throw new Error(`Missing appSetting.basic for groupDisplayId=${payload.groupDisplayId}`);
  }
  return {
    id: payload.groupDisplayId,
    isDisplayLogo: basic.isDisplayLogo,
    logoImage: basic.logoImage,
    isDisplayRequirement: basic.isDisplayRequirement,
    isRequiredCompanyName: basic.isRequiredCompanyName,
    isRequiredName: basic.isRequiredName,
    isRequiredRequirement: basic.isRequiredRequirement,
    isDisplayTopGroupName: basic.isDisplayTopGroupName,
    isDisplayGroupName: basic.isDisplayGroupName,
    isDisplayCompleteReception: basic.isDisplayCompleteReception,
    isPhoneBookEnabled: basic.isPhoneBookEnabled,
  };
};

/**
 * APIレスポンスのアプリ設定リストをIndexedDB保存用の正規化済みオブジェクトに変換する。
 * groupDisplayId=0のアイテムからsettingCommon・standby・callInfo・receptionCommonWordを抽出し、
 * 全アイテムからmenuWords・deliveryWords・menuButtons・basicsを配列として構築する。
 * @param appSettings - APIから取得したアプリ設定レスポンス
 * @returns 正規化されたAppSettingsNormalized
 */
const normalizeAppSettings = (appSettings: AppSettingsResponse): AppSettingsNormalized => {
  const normalized: AppSettingsNormalized = {
    menuWords: [],
    deliveryWords: [],
    menuButtons: [],
    basics: [],
  };

  for (const item of appSettings.data.list) {
    if (item.groupDisplayId === 0) {
      normalized.settingCommon = createSettingCommonEntity(item);
      normalized.standby = createStandbyEntity(item);
      normalized.callInfo = createCallInfoEntity(item);
      normalized.receptionCommonWord = createReceptionCommonWordEntity(item);
    }

    normalized.menuWords.push(createMenuWordEntity(item));
    normalized.deliveryWords.push(createDeliveryWordEntity(item));
    normalized.menuButtons.push(createSettingMenuButtonEntity(item));
    normalized.basics.push(createSettingBasicEntity(item));
  }

  return normalized;
};

/**
 * APIレスポンスのアプリ設定をIndexedDBに一括保存する。
 * 8つのストアをトランザクションで原子的に書き込み、整合性を保証する。
 * @param appSettings - APIから取得したアプリ設定レスポンス
 * @returns 正規化されたAppSettingsNormalized
 * @throws {Error} 必須エンティティ（settingCommon・standby・callInfo・receptionCommonWord）が欠損している場合
 */
export const saveAppSettingsData = async (
  appSettings: AppSettingsResponse
): Promise<AppSettingsNormalized> => {
  const normalized = normalizeAppSettings(appSettings);
  const db = await getDB();

  if (
    !normalized.settingCommon ||
    !normalized.standby ||
    !normalized.callInfo ||
    !normalized.receptionCommonWord
  ) {
    throw new Error("Missing required app settings entities for transactional save");
  }

  const tx = db.transaction(APP_SETTING_STORES, "readwrite");
  await tx.objectStore(SETTING_COMMON_STORE).put(normalized.settingCommon, RECORD_KEY);
  await tx.objectStore(STANDBY_STORE).put(normalized.standby, RECORD_KEY);
  await tx.objectStore(CALL_INFO_STORE).put(normalized.callInfo, RECORD_KEY);
  await tx.objectStore(RECEPTION_COMMON_WORD_STORE).put(normalized.receptionCommonWord, RECORD_KEY);
  await tx.objectStore(MENU_WORDS_STORE).put(normalized.menuWords, RECORD_KEY);
  await tx.objectStore(DELIVERY_WORDS_STORE).put(normalized.deliveryWords, RECORD_KEY);
  await tx.objectStore(MENU_BUTTONS_STORE).put(normalized.menuButtons, RECORD_KEY);
  await tx.objectStore(BASICS_STORE).put(normalized.basics, RECORD_KEY);
  await tx.done;

  // deliveryMapImagePath（署名付きURL・期限あり）をBlobとしてダウンロードしてキャッシュする。
  // 失敗時はキャッシュをそのままにしてフォールバックを許容するため、
  // 画像キャッシュ同期の例外は握りつぶしてアプリ設定の保存自体は成功扱いとする。
  try {
    await syncDeliveryMapImageCache(normalized.menuButtons);
  } catch (error) {
    console.error(
      "[deliveryMapImage] cache sync failed, app settings save succeeds anyway:",
      error
    );
  }

  return normalized;
};

/**
 * menuButtons の deliveryMapImagePath を会社（companyId = menuButton.id）ごとに確認し、
 * 必要に応じて画像をダウンロードしてBlobをIDBに保存する。
 *
 * モバイル実装と同様に各会社（親会社 id=0 と子会社 id=1, 2, 3, ...）の画像を個別にキャッシュし、
 * IDB のキーには `companyId.toString()` を使う。
 *
 * - URLが前回保存時と同じ場合はダウンロードをスキップする
 * - URLがnullの場合は該当会社のキャッシュをクリアする
 * - ダウンロードに失敗した場合は既存キャッシュを保持する（古いURLが失効していても直前のBlobを表示可能）
 * - 今回の menuButtons に含まれない会社の古いキャッシュは削除する（孤児エントリ防止）
 */
const syncDeliveryMapImageCache = async (menuButtons: SettingMenuButtonEntity[]): Promise<void> => {
  const db = await getDB();
  const existingKeys = await db.getAllKeys(DELIVERY_MAP_IMAGE_STORE);
  const currentKeys = new Set(menuButtons.map((mb) => mb.id.toString()));

  // 古い会社（今回のレスポンスに無いID）のキャッシュを削除
  for (const key of existingKeys) {
    if (!currentKeys.has(key.toString())) {
      await db.delete(DELIVERY_MAP_IMAGE_STORE, key);
    }
  }

  // 各会社の画像を並列で同期する（1社の失敗が他社に影響しないように individual try-catch）
  await Promise.all(
    menuButtons.map(async (mb) => {
      const key = mb.id.toString();
      const url = mb.deliveryMapImagePath ?? null;

      if (!url) {
        await db.delete(DELIVERY_MAP_IMAGE_STORE, key);
        return;
      }

      const cached = await db.get(DELIVERY_MAP_IMAGE_STORE, key);
      if (cached?.originalUrl === url) return; // URL変わっていなければスキップ

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`fetch failed: ${response.status}`);
        const blob = await response.blob();
        await db.put(
          DELIVERY_MAP_IMAGE_STORE,
          { originalUrl: url, blob, fetchedAt: Date.now() },
          key
        );
      } catch (error) {
        console.error(
          `[deliveryMapImage] download failed for companyId=${mb.id}, keeping previous cache:`,
          error
        );
      }
    })
  );
};

/**
 * 指定会社のdeliveryMapImage キャッシュを取得する。
 * @param companyId menuButton.id（=groupDisplayId）。親会社は 0、子会社は 1, 2, 3...
 */
export const getDeliveryMapImageCache = async (
  companyId: number
): Promise<DeliveryMapImageCacheEntity | undefined> => {
  const db = await getDB();
  return db.get(DELIVERY_MAP_IMAGE_STORE, companyId.toString());
};

/**
 * 全会社のdeliveryMapImage キャッシュをクリアする。
 */
export const clearDeliveryMapImageCache = async (): Promise<void> => {
  const db = await getDB();
  await db.clear(DELIVERY_MAP_IMAGE_STORE);
};

/**
 * IndexedDBから全アプリ設定を並列取得し、正規化済みオブジェクトとして返す。
 * すべてのストアが空の場合はundefinedを返す。
 * @returns AppSettingsNormalized。全ストアが空の場合はundefined
 */
export const getAppSettingsData = async (): Promise<AppSettingsNormalized | undefined> => {
  const db = await getDB();
  const [
    settingCommon,
    standby,
    callInfo,
    receptionCommonWord,
    menuWords,
    deliveryWords,
    menuButtons,
    basics,
  ] = await Promise.all([
    db.get(SETTING_COMMON_STORE, RECORD_KEY),
    db.get(STANDBY_STORE, RECORD_KEY),
    db.get(CALL_INFO_STORE, RECORD_KEY),
    db.get(RECEPTION_COMMON_WORD_STORE, RECORD_KEY),
    db.get(MENU_WORDS_STORE, RECORD_KEY),
    db.get(DELIVERY_WORDS_STORE, RECORD_KEY),
    db.get(MENU_BUTTONS_STORE, RECORD_KEY),
    db.get(BASICS_STORE, RECORD_KEY),
  ]);

  const hasAnyData = [
    settingCommon,
    standby,
    callInfo,
    receptionCommonWord,
    menuWords,
    deliveryWords,
    menuButtons,
    basics,
  ].some((value) => value !== undefined);

  if (!hasAnyData) {
    return undefined;
  }

  return {
    settingCommon,
    standby,
    callInfo,
    receptionCommonWord,
    menuWords: menuWords ?? [],
    deliveryWords: deliveryWords ?? [],
    menuButtons: menuButtons ?? [],
    basics: basics ?? [],
  };
};

/**
 * IndexedDBからメニューボタンエンティティの配列を取得する。
 * @returns SettingMenuButtonEntityの配列。存在しない場合はundefined
 */
export const getMenuButton = async (): Promise<SettingMenuButtonEntity[] | undefined> => {
  const db = await getDB();
  return db.get(MENU_BUTTONS_STORE, RECORD_KEY);
};

/**
 * IndexedDBからメニューワードエンティティの配列を取得する。
 * @returns MenuWordEntityの配列。存在しない場合はundefined
 */
export const getMenuWords = async (): Promise<MenuWordEntity[] | undefined> => {
  const db = await getDB();
  return db.get(MENU_WORDS_STORE, RECORD_KEY);
};

/**
 * IndexedDBから受付共通ワードエンティティを取得する。
 * @returns ReceptionCommonWordEntity。存在しない場合はundefined
 */
export const getReceptionCommonWord = async (): Promise<ReceptionCommonWordEntity | undefined> => {
  const db = await getDB();
  return db.get(RECEPTION_COMMON_WORD_STORE, RECORD_KEY);
};

/**
 * IndexedDBからすべてのアプリ設定データを削除する。
 * 8つのアプリ設定ストアをトランザクションで一括削除する。
 */
export const clearAppSettingsData = async (): Promise<void> => {
  const db = await getDB();
  const stores = [...APP_SETTING_STORES, DELIVERY_MAP_IMAGE_STORE] as const;
  const tx = db.transaction(stores, "readwrite");
  stores.forEach((storeName) => {
    // deliveryMapImage は会社IDをキーに複数エントリを持つため clear() で全削除する。
    if (storeName === DELIVERY_MAP_IMAGE_STORE) {
      tx.objectStore(storeName).clear();
    } else {
      tx.objectStore(storeName).delete(RECORD_KEY);
    }
  });
  await tx.done;
};

/**
 * IndexedDBの共通設定エンティティを上書き更新する。
 * @param data - 更新後のSettingCommonEntity
 */
export const updateSettingCommon = async (data: SettingCommonEntity): Promise<void> => {
  const db = await getDB();
  await db.put(SETTING_COMMON_STORE, data, RECORD_KEY);
};

/**
 * IndexedDBのスタンバイ設定エンティティを上書き更新する。
 * @param data - 更新後のStandbyEntity
 */
export const updateStandby = async (data: StandbyEntity): Promise<void> => {
  const db = await getDB();
  await db.put(STANDBY_STORE, data, RECORD_KEY);
};

/**
 * IndexedDBの通話設定エンティティを上書き更新する。
 * @param data - 更新後のCallInfoEntity
 */
export const updateCallInfo = async (data: CallInfoEntity): Promise<void> => {
  const db = await getDB();
  await db.put(CALL_INFO_STORE, data, RECORD_KEY);
};

/**
 * IndexedDBの受付共通ワードエンティティを上書き更新する。
 * @param data - 更新後のReceptionCommonWordEntity
 */
export const updateReceptionCommonWord = async (data: ReceptionCommonWordEntity): Promise<void> => {
  const db = await getDB();
  await db.put(RECEPTION_COMMON_WORD_STORE, data, RECORD_KEY);
};

/**
 * IndexedDBのメニューワード配列を上書き更新する。
 * @param data - 更新後のMenuWordEntity配列
 */
export const updateMenuWords = async (data: MenuWordEntity[]): Promise<void> => {
  const db = await getDB();
  await db.put(MENU_WORDS_STORE, data, RECORD_KEY);
};

/**
 * IndexedDBの配達ワード配列を上書き更新する。
 * @param data - 更新後のDeliveryWordEntity配列
 */
export const updateDeliveryWords = async (data: DeliveryWordEntity[]): Promise<void> => {
  const db = await getDB();
  await db.put(DELIVERY_WORDS_STORE, data, RECORD_KEY);
};

/**
 * IndexedDBのメニューボタン設定配列を上書き更新する。
 * @param data - 更新後のSettingMenuButtonEntity配列
 */
export const updateMenuButtons = async (data: SettingMenuButtonEntity[]): Promise<void> => {
  const db = await getDB();
  await db.put(MENU_BUTTONS_STORE, data, RECORD_KEY);
};

/**
 * IndexedDBの基本設定配列を上書き更新する。
 * @param data - 更新後のSettingBasicEntity配列
 */
export const updateBasics = async (data: SettingBasicEntity[]): Promise<void> => {
  const db = await getDB();
  await db.put(BASICS_STORE, data, RECORD_KEY);
};

/**
 * 電話帳の最終更新日時を現在時刻に設定して「今更新した」ことをマークする。
 * iOS の挙動に合わせ、コンテンツ保存時に `lastUpdateDate` を現在時刻に設定する。
 */
export const touchPhoneBookAutoUpdateNow = async (): Promise<void> => {
  const db = await getDB();
  const current = await db.get(PHONE_BOOK_AUTO_UPDATE_STORE, RECORD_KEY);
  const payload: PhoneBookAutoUpdateEntity = {
    updateDate: current?.updateDate ?? null,
    lastUpdateDate: new Date().toISOString(),
  };
  await db.put(PHONE_BOOK_AUTO_UPDATE_STORE, payload, RECORD_KEY);
};

/**
 * アプリ設定の最終更新日時を現在時刻に設定して「今更新した」ことをマークする。
 * iOS の挙動に合わせ、コンテンツ保存時に `lastUpdateDate` を現在時刻に設定する。
 */
export const touchAppSettingAutoUpdateNow = async (): Promise<void> => {
  const db = await getDB();
  const current = await db.get(APP_SETTING_AUTO_UPDATE_STORE, RECORD_KEY);
  const payload: AppSettingAutoUpdateObject = {
    updateDate: current?.updateDate ?? null,
    lastUpdateDate: new Date().toISOString(),
  };
  await db.put(APP_SETTING_AUTO_UPDATE_STORE, payload, RECORD_KEY);
};

const applyDisplayIdDiffToSettings = (
  menuWords: MenuWordEntity[] | undefined,
  deliveryWords: DeliveryWordEntity[] | undefined,
  menuButtons: SettingMenuButtonEntity[] | undefined,
  basics: SettingBasicEntity[] | undefined,
  requireDeleteSettingIdList: number[],
  requireCreateSettingIdList: number[]
): {
  newMenuWords: MenuWordEntity[];
  newDeliveryWords: DeliveryWordEntity[];
  newMenuButtons: SettingMenuButtonEntity[];
  newBasics: SettingBasicEntity[];
} => {
  const currentMenuWords = menuWords ?? [];
  const currentDeliveryWords = deliveryWords ?? [];
  const currentMenuButtons = menuButtons ?? [];
  const currentBasics = basics ?? [];

  if (requireCreateSettingIdList.length > 0) {
    appendCreatedSettingsById(
      currentMenuWords,
      currentDeliveryWords,
      currentMenuButtons,
      currentBasics,
      requireCreateSettingIdList
    );
  }

  const newMenuWords = currentMenuWords.filter((e) => !requireDeleteSettingIdList.includes(e.id));
  const newDeliveryWords = currentDeliveryWords.filter(
    (e) => !requireDeleteSettingIdList.includes(e.id)
  );
  const newMenuButtons = currentMenuButtons.filter(
    (e) => !requireDeleteSettingIdList.includes(e.id)
  );
  const newBasics = currentBasics.filter((e) => !requireDeleteSettingIdList.includes(e.id));

  return {
    newMenuWords,
    newDeliveryWords,
    newMenuButtons,
    newBasics,
  };
};

const appendCreatedSettingsById = (
  currentMenuWords: MenuWordEntity[],
  currentDeliveryWords: DeliveryWordEntity[],
  currentMenuButtons: SettingMenuButtonEntity[],
  currentBasics: SettingBasicEntity[],
  requireCreateSettingIdList: number[]
): void => {
  const templateMenuWord = currentMenuWords.find((e) => e.id === 0);
  const templateDeliveryWord = currentDeliveryWords.find((e) => e.id === 0);
  const templateMenuButton = currentMenuButtons.find((e) => e.id === 0);
  const templateBasic = currentBasics.find((e) => e.id === 0);

  if (!templateMenuWord || !templateDeliveryWord || !templateMenuButton || !templateBasic) {
    throw new Error("Missing template entry (id=0) for app settings sync");
  }

  for (const newId of requireCreateSettingIdList) {
    currentMenuWords.push({ ...templateMenuWord, id: newId });
    currentDeliveryWords.push({ ...templateDeliveryWord, id: newId });
    currentMenuButtons.push({ ...templateMenuButton, id: newId });
    currentBasics.push({ ...templateBasic, id: newId });
  }
};

/**
 * displayId差分に基づいてphoneBooks・menuWords・deliveryWords・menuButtons・basicsを一括同期する。
 * requireDeleteSettingIdListのidを持つエントリを削除し、
 * requireCreateSettingIdListの各idに対してid=0のエントリをテンプレートとして複製・追加する。
 * 書き込みはひとつのトランザクションで実行する（差分計算用の読み込みは事前に行う）。
 * @param phoneBooksData - 保存する電話帳エンティティ
 * @param requireDeleteSettingIdList - 削除対象のdisplayId一覧
 * @param requireCreateSettingIdList - 追加対象のdisplayId一覧
 * @throws {Error} id=0のテンプレートエントリが存在しない場合
 */
export const syncPhonebookAndAppSettingsByDisplayIds = async (
  phoneBooksData: PhoneBooksEntity,
  requireDeleteSettingIdList: number[],
  requireCreateSettingIdList: number[]
): Promise<void> => {
  const db = await getDB();

  const [menuWords, deliveryWords, menuButtons, basics] = await Promise.all([
    db.get(MENU_WORDS_STORE, RECORD_KEY),
    db.get(DELIVERY_WORDS_STORE, RECORD_KEY),
    db.get(MENU_BUTTONS_STORE, RECORD_KEY),
    db.get(BASICS_STORE, RECORD_KEY),
  ]);

  const { newMenuWords, newDeliveryWords, newMenuButtons, newBasics } =
    applyDisplayIdDiffToSettings(
      menuWords,
      deliveryWords,
      menuButtons,
      basics,
      requireDeleteSettingIdList,
      requireCreateSettingIdList
    );

  const tx = db.transaction(PHONEBOOK_AND_MENU_SETTING_STORES, "readwrite");
  await tx.objectStore(PHONE_BOOKS_STORE).put(phoneBooksData, RECORD_KEY);
  await tx.objectStore(MENU_WORDS_STORE).put(newMenuWords, RECORD_KEY);
  await tx.objectStore(DELIVERY_WORDS_STORE).put(newDeliveryWords, RECORD_KEY);
  await tx.objectStore(MENU_BUTTONS_STORE).put(newMenuButtons, RECORD_KEY);
  await tx.objectStore(BASICS_STORE).put(newBasics, RECORD_KEY);
  await tx.done;
};
